<script setup lang="ts">
import { computed, onBeforeMount, ref } from 'vue';
import { useUrlSearchParams } from '@vueuse/core';
import isMobile from 'is-mobile';
import { SCToastNotificationGroup } from '@nsftx/seven-components';
import integrator from '@/services';
import {
  LiwGamesOddsType,
  SevenOddsType,
  type ApiConfiguration,
  type IntegratorResponseData
} from '@/types';
import { env } from '@/utility';
import { getConfiguration } from '@/api';
import { useAuthToken } from '@/composables/useAuthToken';
import { useNotifications } from '@/composables/useNotifications';

const { integrationType: integrationTypeSearchParam } = useUrlSearchParams('history');
const integrationType =
  integrationTypeSearchParam instanceof Array
    ? integrationTypeSearchParam[0]
    : integrationTypeSearchParam;
const isMobileView = isMobile();

const { notifications, setNotification } = useNotifications();
const { sevenToken, integrationToken, userId, tenantUuid } = useAuthToken();
const apiConfig = ref<ApiConfiguration>();
const currency = ref('');
const language = ref('');
const oddsFormat = ref<LiwGamesOddsType>();
const containerClass = computed(() => (integrationType === 'wkwebview' ? 'wkwebview-style' : ''));

const setupSevenIntegration = async () => {
  const {
    auth: { token, id },
    tenant: { uuid },
    currency: { symbol: currencySymbol },
    locale: { iso1: iso639locale },
    settings: { oddType: sevenOddsType }
  } = (await integrator.init(integrationType)) as IntegratorResponseData;

  currency.value = currencySymbol.toLowerCase();
  language.value = iso639locale;
  tenantUuid.value = uuid;
  userId.value = id;
  sevenToken.value = token;

  integrator.notifyAppLoaded();
  setOddsType(sevenOddsType);

  await setConfiguration();
};

const setConfiguration = async () => {
  const { partnerName, gameId, skin, error, message } = await getConfiguration(tenantUuid.value);

  if (error) {
    setNotification(message);
    return;
  }

  apiConfig.value = {
    partnerName,
    gameId,
    skin
  };

  loadLiwGamesScript();
};

const setOddsType = (sevenOddsType: SevenOddsType) => {
  oddsFormat.value = {
    [SevenOddsType.DECIMAL]: LiwGamesOddsType.DECIMAL,
    [SevenOddsType.FRACTIONAL]: LiwGamesOddsType.FRACTIONAL,
    [SevenOddsType.AMERICAN]: LiwGamesOddsType.AMERICAN
  }[sevenOddsType];
};

const loadLiwGamesScript = () => {
  const liwGamesScript = document.createElement('script');

  liwGamesScript.setAttribute('src', env.liwGamesScriptUrl + apiConfig.value?.partnerName);
  document.head.appendChild(liwGamesScript);

  liwGamesScript.onerror = () => {
    setNotification('Failed to load Liw Games script');
  };

  liwGamesScript.onload = () => {
    console.log('Loaded Liw Games script');
    setupLiwGamesWidget();
  };
};

const setupLiwGamesWidget = () => {
  window.MagicGames.addWidget({
    container: 'liw-games-container',
    lang: language.value,
    runFrom:
      integrationType === 'webview' || integrationType === 'wkwebview' ? 4 : isMobileView ? 3 : 2,
    options: {
      game: apiConfig.value?.gameId,
      defaultCurrency: currency.value,
      skin: apiConfig.value?.skin,
      oddCulture: oddsFormat.value
    },
    UserToken: integrationToken.value,
    onLoad: function () {
      console.log('Game is loaded.');
    }
  });
};

onBeforeMount(() => {
  setupSevenIntegration();
});
</script>

<template>
  <div
    id="liw-games-container"
    :class="containerClass"
  ></div>
  <SCToastNotificationGroup
    v-model="notifications"
    position="bottom"
  />
</template>

<style>
.wkwebview-style {
  height: 100vh;
  overflow: auto;
}
.sc-toast-notification-group--bottom {
  bottom: var(--sc-toast-notification-group-offset-b);
}

.sc-alert--toast {
  width: 100% !important;
}
</style>
